import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  MapPinIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  TruckIcon,
  ArrowDownTrayIcon,
  PencilSquareIcon,
  StarIcon,
  TicketIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

import ModalEditCancel from './ModalEditCancel';
import RatingModal from './RatingModal';
import InfoEventModal from './InfoEventModal';
import PaymentContainer from '../../components/PaymentContainer';
import * as API from '../../api';
import { cn } from '../../shared/utils/cn';

const Booking = () => {
  const { t } = useTranslation();
  const [modalShowInfo, toggleModalShowInfo] = React.useState(false);
  const [modalShowEditCancel, toggleModalShowEditCancel] = React.useState(false);
  const [modalShowRating, toggleModalShowRating] = React.useState(false);
  const [loadingUnconfirmedOrders, setLoadingUnconfirmedOrders] = React.useState(false);
  const [ordersUnconfirmed, setOrdersUnconfirmed] = React.useState([]);
  const [loadingConfirmedOrders, setLoadingConfirmedOrders] = React.useState(false);
  const [ordersConfirmed, setOrdersConfirmed] = React.useState([]);
  const [order, setOrder] = React.useState({});

  const loadModalInfo = (o) => {
    setOrder(o);
    toggleModalShowInfo(true);
  };
  const loadModalUpdating = (o) => {
    setOrder(o);
    toggleModalShowEditCancel(true);
  };
  const loadModalReview = (o) => {
    setOrder(o);
    toggleModalShowRating(true);
  };
  const closeModalInfo = () => {
    setOrder({});
    toggleModalShowInfo(false);
  };
  const closeModalUpdating = () => toggleModalShowEditCancel(false);
  const closeModalReview = () => {
    setOrder({});
    toggleModalShowRating(false);
  };

  const downloadBarcode = async (url) => {
    API.downloadBarcodeImage(url)
      .then((response) => {
        const objectUrl = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = objectUrl;
        link.setAttribute('download', 'barcode.png');
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(objectUrl);
        link.remove();
      })
      .catch((err) => console.error('Error in downloading the image: ', err));
  };

  const [downloadingInvoiceId, setDownloadingInvoiceId] = React.useState(null);
  const downloadInvoice = async (reservationId) => {
    setDownloadingInvoiceId(reservationId);
    API.downloadInvoice(reservationId)
      .then((response) => {
        const objectUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = objectUrl;
        link.setAttribute('download', `tafwija-invoice-${reservationId}.pdf`);
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(objectUrl);
        link.remove();
      })
      .catch((err) => console.error('Error downloading invoice: ', err))
      .finally(() => setDownloadingInvoiceId(null));
  };

  React.useEffect(() => {
    setLoadingUnconfirmedOrders(true);
    API.UserOrdersUnconfirmed()
      .then((res) => setOrdersUnconfirmed(res.data))
      .catch(() => {})
      .then(() => setLoadingUnconfirmedOrders(false));

    setLoadingConfirmedOrders(true);
    API.UserOrdersConfirmed()
      .then((res) => setOrdersConfirmed(res.data))
      .catch(() => {})
      .then(() => setLoadingConfirmedOrders(false));
  }, [0]);

  // Pay-now button with payment-status-aware label (preserved from legacy).
  const checkPayment = (o) => {
    const statuses = {
      AUTHORIZE_PENDING: 'En attente',
      AUTHORIZED: 'Autorisé',
      AUTH_REVERSED: 'Inversement',
      CANCELLED: 'Annulée',
      DECLINED: 'Refusé',
      CHARGE_PENDING: 'Charge en cours',
      CHARGED: 'Facturé',
      CHARGED_BACK: 'Remboursé',
      CHARGEBACK_REVERSED: 'Inversement remboursé',
      CHARGEBACK_PENDING: 'Remboursement en attente',
      REFUND_PENDING: 'Remboursement en attente',
      REFUNDED: 'Remboursé',
      ERROR: 'Erreur',
      CREDITED_PENDING: 'Crédit en attente',
      CREDITED: 'Crédité',
    };
    if (o?.etat === 1) return null;
    const paymentStatus = o?.payment_history?.status;
    const buttonText = paymentStatus ? statuses[paymentStatus] || t('account.payNow') : t('account.payNow');
    const isPaymentProcessed = paymentStatus === 'CHARGED';
    return <PaymentContainer buttonText={buttonText} reservation={o} disabled={isPaymentProcessed} />;
  };

  return (
    <div className="space-y-10">
      {/* Upcoming */}
      <section>
        <h2 className="mb-4 font-display text-xl font-semibold text-ink">{t('account.upcomingTitle')}</h2>

        <div className="mb-4 rounded-xl border border-brand-100 bg-brand-50/60 px-4 py-3 text-xs text-brand-700">
          {t('account.cardNotice')}
        </div>

        {loadingUnconfirmedOrders ? (
          <Loading />
        ) : ordersUnconfirmed.length ? (
          <div className="space-y-4">
            {ordersUnconfirmed.map((o, index) => {
              const info = API.OrderInfo(o);
              const end = new Date((o.date ?? o.date_depart_activity) + ' 12:00 ').getTime();
              const diffDays = Math.floor((end - Date.now()) / (1000 * 60 * 60 * 24));
              const confirmed = o.etat == 1;
              return (
                <OrderCard
                  key={index}
                  order={o}
                  info={info}
                  status={confirmed ? 'confirmed' : 'pending'}
                  onInfo={() => loadModalInfo(o)}
                  onDownload={o.bar_code_image ? () => downloadBarcode(o.bar_code_image) : null}
                  onDownloadInvoice={() => downloadInvoice(o.id)}
                  downloadingInvoice={downloadingInvoiceId === o.id}
                  t={t}
                  actions={
                    <>
                      <SecondaryButton onClick={() => loadModalUpdating(o)} disabled={diffDays <= 2} icon={PencilSquareIcon}>
                        {t('account.modify')}
                      </SecondaryButton>
                      {checkPayment(o)}
                    </>
                  }
                />
              );
            })}
          </div>
        ) : (
          <EmptyState t={t} />
        )}
      </section>

      {/* Past & cancelled */}
      <section>
        <h2 className="mb-4 font-display text-xl font-semibold text-ink">{t('account.pastTitle')}</h2>
        {loadingConfirmedOrders ? (
          <Loading />
        ) : ordersConfirmed.length ? (
          <div className="space-y-4">
            {ordersConfirmed.map((o) => {
              const info = API.OrderInfo(o);
              const cancelled = parseInt(o.cancel);
              return (
                <OrderCard
                  key={o.id}
                  order={o}
                  info={info}
                  status={cancelled ? 'cancelled' : 'confirmed'}
                  onInfo={() => loadModalInfo(o)}
                  onDownload={o.bar_code_image ? () => downloadBarcode(o.bar_code_image) : null}
                  onDownloadInvoice={() => downloadInvoice(o.id)}
                  downloadingInvoice={downloadingInvoiceId === o.id}
                  t={t}
                  actions={
                    <SecondaryButton onClick={() => loadModalReview(o)} disabled={!!cancelled} icon={StarIcon}>
                      {t('account.review')}
                    </SecondaryButton>
                  }
                />
              );
            })}
          </div>
        ) : (
          <EmptyState t={t} />
        )}
      </section>

      <InfoEventModal order={order} shown={modalShowInfo} close={closeModalInfo} />
      <RatingModal order={order} shown={modalShowRating} close={closeModalReview} />
      <ModalEditCancel order={order} shown={modalShowEditCancel} close={closeModalUpdating} />
    </div>
  );
};

// ---- order card -------------------------------------------------------------

function OrderCard({ order, info, status, actions, onInfo, onDownload, onDownloadInvoice, downloadingInvoice, t }) {
  const isSejour = order.event?.type === 'Sejour';
  const hasDeposit = Math.round(info.totalPricePay) > 0;
  const dateLabel = order.date ? new Date(order.date).toLocaleDateString('fr-FR') : null;
  // Invoice only makes sense once money genuinely moved online — pay-on-site
  // / bank-transfer / still-pending reservations have nothing to invoice yet
  // (the backend 404s on those too, this just keeps the button from
  // appearing where it would only ever fail).
  const isPaidOnline = order?.payment_history?.status === 'CHARGED';

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-card">
      <div className="flex flex-col gap-4 p-4 sm:flex-row sm:p-5">
        {/* image */}
        <div className="h-32 w-full shrink-0 overflow-hidden rounded-xl sm:h-28 sm:w-40">
          <img src={order.event?.image} alt={order.event?.title} className="h-full w-full object-cover" />
        </div>

        {/* body */}
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs text-ink-subtle">
                {t('account.orderNumber')} #{order.id}
              </p>
              <h3 className="truncate font-display text-base font-semibold text-ink">{order.event?.title}</h3>
            </div>
            <StatusBadge status={status} t={t} />
          </div>

          {/* meta */}
          <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-ink-muted">
            {isSejour ? (
              <>
                {order.check_in ? <Meta icon={CalendarDaysIcon}>{order.check_in}</Meta> : null}
                {order.check_out ? <Meta icon={CalendarDaysIcon}>{order.check_out}</Meta> : null}
              </>
            ) : dateLabel ? (
              <Meta icon={CalendarDaysIcon}>{dateLabel}</Meta>
            ) : null}
            {order.depart_city && order.depart_city !== '1' ? <Meta icon={MapPinIcon}>{order.depart_city}</Meta> : null}
            {info.roader ? <Meta icon={TruckIcon}>{info.roader}</Meta> : null}
            <Meta icon={UserGroupIcon}>{info.totalPlaces}</Meta>
          </div>

          {/* price + actions */}
          <div className="mt-3 flex flex-wrap items-end justify-between gap-3 border-t border-slate-100 pt-3">
            <div>
              <p className="text-[11px] uppercase tracking-wide text-ink-subtle">{t('account.total')}</p>
              <bdi className="font-display text-lg font-bold text-brand-500">
                {API.priceFormat(info.totalPriceByDiscount)}
              </bdi>
              {hasDeposit ? (
                <p className="text-[11px] text-ink-subtle">
                  {t('account.advanceToPay')}: <bdi>{API.priceFormat(info.totalPricePay)}</bdi>
                </p>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {isPaidOnline && onDownloadInvoice ? (
                <button
                  type="button"
                  onClick={onDownloadInvoice}
                  disabled={downloadingInvoice}
                  className="inline-flex items-center gap-1.5 rounded-full border border-accent-500 bg-accent-50 px-3.5 py-2 text-xs font-semibold text-accent-700 transition-colors hover:bg-accent-100 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {downloadingInvoice ? (
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-accent-300 border-t-accent-700" />
                  ) : (
                    <DocumentTextIcon className="h-4 w-4" />
                  )}
                  {t('account.downloadInvoice')}
                </button>
              ) : null}
              {onDownload ? (
                <button
                  type="button"
                  onClick={onDownload}
                  className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3.5 py-2 text-xs font-semibold text-ink transition-colors hover:border-brand-500 hover:text-brand-500"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  {t('account.downloadTicket')}
                </button>
              ) : null}
              {actions}
              <button
                type="button"
                onClick={onInfo}
                className="inline-flex items-center gap-1.5 rounded-full bg-brand-500 px-3.5 py-2 text-xs font-semibold text-white transition-colors hover:bg-brand-600"
              >
                <TicketIcon className="h-4 w-4" />
                {t('account.viewAll')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status, t }) {
  const map = {
    pending: { label: t('account.statusPending'), icon: ClockIcon, cls: 'bg-amber-50 text-amber-700' },
    confirmed: { label: t('account.statusConfirmed'), icon: CheckCircleIcon, cls: 'bg-emerald-50 text-emerald-700' },
    cancelled: { label: t('account.statusCancelled'), icon: XCircleIcon, cls: 'bg-rose-50 text-rose-600' },
  };
  const s = map[status] || map.pending;
  const Icon = s.icon;
  return (
    <span className={cn('inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold', s.cls)}>
      <Icon className="h-3.5 w-3.5" />
      {s.label}
    </span>
  );
}

function Meta({ icon: Icon, children }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <Icon className="h-3.5 w-3.5 text-brand-500" />
      {children}
    </span>
  );
}

function SecondaryButton({ onClick, disabled, icon: Icon, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3.5 py-2 text-xs font-semibold text-ink transition-colors hover:border-brand-500 hover:text-brand-500 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:text-ink"
    >
      {Icon ? <Icon className="h-4 w-4" /> : null}
      {children}
    </button>
  );
}

function Loading() {
  return (
    <div className="space-y-4">
      {Array.from({ length: 2 }).map((_, i) => (
        <div key={i} className="flex gap-4 rounded-2xl border border-slate-100 bg-white p-5 shadow-card">
          <div className="h-28 w-40 shrink-0 animate-pulse rounded-xl bg-slate-100" />
          <div className="flex-1 space-y-3">
            <div className="h-4 w-2/5 animate-pulse rounded bg-slate-100" />
            <div className="h-3 w-1/3 animate-pulse rounded bg-slate-100" />
            <div className="h-6 w-1/4 animate-pulse rounded bg-slate-100" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ t }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-14 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-surface-muted">
        <TicketIcon className="h-6 w-6 text-ink-subtle" />
      </div>
      <p className="mt-4 text-sm text-ink-muted">{t('account.empty')}</p>
      <Link
        to="/events"
        className="mt-4 rounded-full bg-brand-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-600"
      >
        {t('account.emptyCta')}
      </Link>
    </div>
  );
}

export default Booking;
